// src/components/Hero.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import LocationMap from './LocationMap';
import { motion, AnimatePresence } from 'framer-motion';

const Hero = () => {
  const [showLocations, setShowLocations] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null); // Add this line

  // Hero slides data
  const heroSlides = [
    {
      title: "Hope. Healing. Innovation.",
      subtitle: "You deserve relief. Let's take the first step together.",
      image: "/img/hero-image.png",
      primaryButton: {
        text: "Schedule an Appointment",
        link: "/newpatients"
      },
      secondaryButton: {
        text: "View Locations",
        action: () => setShowLocations(true)
      }
    },
    {
      title: "TMS Treatments for Autism!",
      subtitle: "Real Results for Real People",
      image: "/img/hero-tms-autism.png",
      primaryButton: {
        text: "Schedule an Appointment",
        link: "/newpatients"
      },
      secondaryButton: {
        text: "Learn More",
        link: "https://revolutiontreatments.com/tms-autism-protocol"
      }
    },
    {
      title: "Now Accepting Mercy Care!",
      subtitle: "Call today to learn more!",
      image: "/img/hero-mercy-care.png",
      primaryButton: {
        text: "Schedule an Appointment",
        link: "/newpatients"
      },
      secondaryButton: {
        text: "View Locations",
        action: () => setShowLocations(true)
      }
    },
    {
      title: "Now Enrolling:",
      subtitle: "A non-drug depression study",
      disclaimer: "Disclaimer: This is not our study. It is offered through a third party.",
      image: "https://soterixmedical.com/depression/static/images/soterix-logo.png",
      primaryButton: {
        text: "See if you qualify",
        link: "https://soterixmedical.com/depression/clinicaltrial"
      },
      secondaryButton: {
        text: "",
        action: null
      }
    }
  ];

  // Auto-advance slides every 10 seconds
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Start new interval
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000); // 10 seconds

    // Cleanup on unmount
    return () => clearInterval(intervalRef.current);
  }, [heroSlides.length, currentSlide]);

  // Helper to reset timer and go to next slide
  const resetIntervalAndSetSlide = (setter) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setter();
  };

  const nextSlide = () => {
    resetIntervalAndSetSlide(() =>
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
    );
  };

  const prevSlide = () => {
    resetIntervalAndSetSlide(() =>
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)
    );
  };

  const goToSlide = (index) => {
    resetIntervalAndSetSlide(() => setCurrentSlide(index));
  };

  const currentHero = heroSlides[currentSlide];

  return (
    <>
      <div id="hero-container" className="container-fluid" style={{ position: 'relative' }}>
        <div className="container">
          <div className="hero-content-wrapper">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                style={{ display: 'flex', width: '100%' }}
              >
                <div className="hero-text-section">
                  <h1>{currentHero.title}</h1>
                  <p  style={{ marginBottom: '0px' }}>
                    {currentHero.subtitle}
                    {currentHero.disclaimer && <span style={{ color: '#888', fontSize: '0.95em', marginTop: '-15px' }}> *</span>}
                  </p>
                  {currentHero.disclaimer && (
                    <p style={{ fontSize: '0.95em', color: '#888', marginTop: '-10px' }}>
                      {currentHero.disclaimer}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <a href={currentHero.primaryButton.link} className="button blue" target="_blank" rel="noopener noreferrer">
                      {currentHero.primaryButton.text}
                    </a>
                    {currentHero.secondaryButton.text && currentHero.secondaryButton.action && (
                      <button 
                        className="button blue" 
                        onClick={currentHero.secondaryButton.action}
                      >
                        {currentHero.secondaryButton.text}
                      </button>
                    )}
                  </div>
                </div>
                <div className="hero-image-section">
                  <LazyLoadImage 
                    alt="Hero" 
                    effect="opacity" 
                    src={currentHero.image} 
                    className={`hero-image ${[3, 4].includes(currentSlide) ? 'hero-image-rounded' : 'hero-image-sharp'}`}
                    style={{ objectFit: 'contain', borderRadius: 20 }}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          className="hero-nav-arrow hero-nav-prev"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button 
          className="hero-nav-arrow hero-nav-next"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          ›
        </button>

        {/* Slide Indicators */}
        <div className="hero-indicators">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Locations Modal */}
      {showLocations && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowLocations(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
              <div
                className="modal-header text-white"
                style={{
                  background: '#3a66bd',
                  borderTopLeftRadius: '20px',
                  borderTopRightRadius: '20px',
                }}
              >
                <h5 className="modal-title text-center w-100" style={{ fontWeight: 600 }}>
                  Our Locations
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowLocations(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body bg-light">
                <div className="container">
                  <LocationMap />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .hero-content-wrapper {
          display: flex;
          align-items: center;
          min-height: 500px;
          gap: 40px;
        }

        .hero-text-section {
          flex: 1;
          min-height: 250px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .hero-text-section h1 {
          min-height: 80px;
          display: flex;
          align-items: center;
          margin-bottom: 20px;
        }

        .hero-text-section p {
          min-height: 60px;
          display: flex;
          align-items: center;
          margin-bottom: 30px;
        }

        .hero-image-section {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .hero-image {
          width: 100%;
          height: 320px;
          transition: opacity 0.5s ease-in-out;
          object-fit: contain;
          object-position: center;
        }

        .hero-image-rounded {
          border-radius: 30px !important;
        }

        .hero-image-sharp {
          border-radius: 0px !important;
          object-fit: contain !important;
          object-position: center !important;
        }

        .hero-nav-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: #3a66bd;
          color: white;
          border: none;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          font-size: 24px;
          font-weight: bold;
          cursor: pointer;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transition: all 0.3s ease;
        }

        .hero-nav-arrow:hover {
          background: #2951a3;
          transform: translateY(-50%) scale(1.05);
        }

        .hero-nav-prev {
          left: 20px;
        }

        .hero-nav-next {
          right: 20px;
        }

        .hero-indicators {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .hero-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .hero-indicator.active {
          background: white;
        }

        .hero-indicator:hover {
          background: rgba(255, 255, 255, 0.7);
        }

        @media (max-width: 768px) {
          .hero-content-wrapper {
            flex-direction: column;
            min-height: 450px;
            text-align: center;
          }

          .hero-text-section {
            min-height: 200px;
          }

          .hero-text-section h1 {
            min-height: 60px;
          }

          .hero-text-section p {
            min-height: 50px;
          }

          .hero-image {
            height: 250px;
          }

          .hero-nav-arrow {
            width: 40px;
            height: 40px;
          }
          
          .hero-nav-prev {
            left: 10px;
          }
          
          .hero-nav-next {
            right: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default Hero;